'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["initialCountryCode", "countrySelectProps", "extensionInputProps", "extensionValue", "phoneInputProps", "onChange", "validCountries", "supportedCountrySet"];
import I18n from 'I18n';
import * as TelephoneData from 'I18n/constants/TelephoneData';
import formatPhoneNumber from 'I18n/utils/formatPhoneNumber';
import guessCountryFromDialCode from 'I18n/utils/guessCountryFromDialCode';
import { Select } from 'foundations-components/transitional/components/Select';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import { View } from 'foundations-components/transitional/components/View';
import { Component, createRef } from 'react';
import SupportedPhoneNumbers from '../constants/SupportedPhoneNumbers';
import getPhoneOptions from '../internal/utils/getPhoneOptions';
import CountryFlag from './CountryFlag';
import { WarningIcon } from 'foundations-assets/icons/components/WarningIcon';
import { InputGroup } from 'foundations-components/transitional/components/InputGroup';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const allCountries = TelephoneData.allCountries;
const iso2Lookup = TelephoneData.iso2Lookup;
const INVALID_OPTION = {
  dialCode: '',
  priority: 10001,
  iso2: null
};
const getDigitsOnly = inputNumber => {
  return inputNumber.replace(/\D/g, '');
};
const shouldUseInitialCountryCode = initialCountryCode => {
  return initialCountryCode && iso2Lookup[initialCountryCode] !== undefined && allCountries[iso2Lookup[initialCountryCode]] !== undefined;
};
const shouldGuessCountryFromDialCode = inputNumber => {
  return inputNumber === '' || inputNumber.indexOf('+') === 0;
};
const getSelectedCountryGuess = (initialCountryCode, inputNumber) => {
  if (shouldUseInitialCountryCode(initialCountryCode)) {
    return allCountries[iso2Lookup[initialCountryCode]];
  } else if (shouldGuessCountryFromDialCode(inputNumber)) {
    return guessCountryFromDialCode(getDigitsOnly(inputNumber));
  }
  return INVALID_OPTION;
};
const _mapPropsToState = (props, initial = false) => {
  const {
    value,
    initialCountryCode
  } = props;
  const inputNumber = value || '';
  const selectedCountryGuess = getSelectedCountryGuess(initialCountryCode, inputNumber);
  const numberToFormat = inputNumber ? getDigitsOnly(inputNumber) : selectedCountryGuess.dialCode;
  const formattedNumber = formatPhoneNumber(numberToFormat, selectedCountryGuess.format);
  const state = {
    selectedCountry: selectedCountryGuess,
    formattedNumber
  };
  if (initial) {
    state.propsMemo = props.value;
  }
  return state;
};
export default class PhoneNumberInput extends Component {
  constructor(props, context) {
    super(props, context);
    this._cursorToEnd = () => {
      const input = this.numberInputRef.current;
      if (input) {
        input.focus();
      }
    };
    this.formatSimpleNumber = (number, hasSelectedValidCountry = true) => {
      const digitsOnly = getDigitsOnly(number);
      if (!hasSelectedValidCountry) {
        return digitsOnly;
      }
      return `+${digitsOnly}`;
    };
    this.handlePhoneNumberInput = event => {
      const {
        formattedNumber,
        selectedCountry
      } = this.state;
      const extension = this.props.extensionValue;
      const eventTarget = event.target;
      if (eventTarget.value === formattedNumber) {
        return;
      }
      let inputNumber = getDigitsOnly(eventTarget.value);
      if (inputNumber.indexOf(selectedCountry.dialCode) !== 0) {
        inputNumber = selectedCountry.dialCode;
      }
      if (this.props.onChange) {
        this.props.onChange({
          phoneNumber: this.formatSimpleNumber(inputNumber, selectedCountry.dialCode),
          extension,
          country: selectedCountry.iso2
        });
      }
      const selectionStart = eventTarget.selectionStart;
      const formattedInputNumber = formatPhoneNumber(inputNumber, selectedCountry.format);
      this.setState({
        formattedNumber: formattedInputNumber
      }, () => {
        let newSelectionStart = selectionStart;
        if (inputNumber === selectedCountry.dialCode || selectionStart === formattedNumber.length + 1) {
          newSelectionStart = formattedInputNumber.length + 1;
        }
        eventTarget.selectionStart = newSelectionStart;
        eventTarget.selectionEnd = newSelectionStart;
      });
    };
    this.handleExtensionInput = e => {
      const {
        selectedCountry,
        formattedNumber
      } = this.state;
      this.props.onChange({
        phoneNumber: this.formatSimpleNumber(formattedNumber, selectedCountry.dialCode),
        extension: getDigitsOnly(e.target.value),
        country: selectedCountry.iso2
      });
    };
    this.handleFlagItemClick = e => {
      const extension = this.props.extensionValue;
      const {
        selectedCountry,
        formattedNumber
      } = this.state;
      const nextSelectedCountry = allCountries[iso2Lookup[e.target.value]];
      if (selectedCountry.iso2 !== nextSelectedCountry.iso2) {
        const newNumber = selectedCountry.dialCode ? formattedNumber.replace(/\D/g, '').replace(selectedCountry.dialCode, nextSelectedCountry.dialCode) : `${nextSelectedCountry.dialCode}${formattedNumber}`;
        const newFormattedNumber = formatPhoneNumber(newNumber, nextSelectedCountry.format);
        this.setState({
          selectedCountry: nextSelectedCountry,
          formattedNumber: newFormattedNumber
        }, () => {
          this._cursorToEnd();
          if (this.props.onChange) {
            this.props.onChange({
              phoneNumber: this.formatSimpleNumber(newNumber, selectedCountry.dialCode || nextSelectedCountry.dialCode),
              extension,
              country: nextSelectedCountry.iso2
            });
          }
        });
      }
    };
    this.getCountryDropDownList = (hasSelectedValidCountry, validCountries, supportedCountrySet) => {
      let countryDropDownList = getPhoneOptions(allCountries);
      const checkSupportedCountries = country => SupportedPhoneNumbers[supportedCountrySet].indexOf(country.value) >= 0;
      const checkValidCountries = country => validCountries.indexOf(country.value) >= 0;
      if (supportedCountrySet && validCountries) {
        countryDropDownList = countryDropDownList.filter(country => checkSupportedCountries(country) && checkValidCountries(country));
      } else if (supportedCountrySet) {
        countryDropDownList = countryDropDownList.filter(checkSupportedCountries);
      } else if (validCountries) {
        countryDropDownList = countryDropDownList.filter(checkValidCountries);
      }
      if (!hasSelectedValidCountry) {
        return [{
          value: INVALID_OPTION.iso2,
          text: I18n.text('i18nAddon.countryInfo.invalidCountryOption')
        }, ...countryDropDownList];
      }
      return countryDropDownList;
    };
    this.buildValueRenderer = countryIso => {
      return () => {
        if (!countryIso) {
          return /*#__PURE__*/_jsx(WarningIcon, {});
        }
        return /*#__PURE__*/_jsx(CountryFlag, {
          countryCode: countryIso
        });
      };
    };
    this.renderExtension = (extensionValue, extensionInputProps) => {
      if (typeof extensionValue !== 'string') {
        return null;
      }
      return /*#__PURE__*/_jsx(FormControl, {
        "aria-label": I18n.text('i18nAddon.phoneNumberInput.ariaLabels.extension'),
        children: /*#__PURE__*/_jsx(TextInput, Object.assign({
          name: "extension-input",
          "data-test-id": "extension-input",
          display: "inline-block",
          maxInlineSize: "124px",
          borderLeftColor: "transparent",
          textOverflow: "ellipsis"
        }, extensionInputProps, {
          placeholder: I18n.text('i18nAddon.phoneNumberInput.extensionPlaceholder'),
          onChange: this.handleExtensionInput,
          value: extensionValue
        }))
      });
    };
    this.state = _mapPropsToState(props, true);
  }
  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.value === null || nextProps.value === undefined) {
      return Object.assign({}, prevState, _mapPropsToState(nextProps));
    } else {
      const nextValueDigits = getDigitsOnly(nextProps.value);
      if (prevState.propsMemo !== nextProps.value) {
        const formatted = formatPhoneNumber(nextValueDigits, prevState.selectedCountry.format);
        return Object.assign({}, prevState, {
          formattedNumber: formatted,
          propsMemo: nextProps.value
        });
      }
    }
    return null;
  }
  render() {
    const {
      selectedCountry,
      formattedNumber
    } = this.state;
    const _this$props = this.props,
      {
        countrySelectProps,
        extensionInputProps,
        extensionValue,
        phoneInputProps,
        validCountries,
        supportedCountrySet
      } = _this$props,
      passThroughProps = _objectWithoutPropertiesLoose(_this$props, _excluded);
    this.numberInputRef = this.numberInputRef || /*#__PURE__*/createRef();
    return /*#__PURE__*/_jsx(View, Object.assign({
      "data-test-id": "phone-number-input"
    }, passThroughProps, {
      display: "inline-flex",
      children: /*#__PURE__*/_jsxs(InputGroup, {
        "data-test-id": "phone-number-input-group",
        use: "itemBoth",
        children: [/*#__PURE__*/_jsx(FormControl, {
          "aria-label": I18n.text('i18nAddon.phoneNumberInput.ariaLabels.country'),
          children: /*#__PURE__*/_jsx(Select, Object.assign({
            name: "country-select",
            "data-test-id": "country-select",
            display: "inline-block",
            position: "relative",
            inlineSize: "auto",
            blockSize: "100%",
            menuWidth: "360px"
          }, countrySelectProps, {
            onChange: this.handleFlagItemClick,
            options: this.getCountryDropDownList(selectedCountry.dialCode, validCountries, supportedCountrySet),
            value: selectedCountry.iso2,
            valueRenderer: this.buildValueRenderer(selectedCountry.iso2)
          }))
        }), /*#__PURE__*/_jsx(FormControl, {
          "aria-label": I18n.text('i18nAddon.phoneNumberInput.ariaLabels.number'),
          children: /*#__PURE__*/_jsx(TextInput, Object.assign({
            name: "phone-number-input",
            "data-test-id": "phone-text-input",
            display: "inline-block",
            inlineSize: "auto",
            inputRef: this.numberInputRef,
            onChange: this.handlePhoneNumberInput,
            value: formattedNumber,
            type: "tel",
            textOverflow: "ellipsis"
          }, phoneInputProps))
        }), this.renderExtension(extensionValue, extensionInputProps)]
      })
    }));
  }
}
PhoneNumberInput.defaultProps = {
  countrySelectProps: {},
  extensionInputProps: {},
  phoneInputProps: {}
};